import { X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useScrollLock } from "../../context/SmoothScrollContext";
import { cn } from "../../utils/format";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Usado como aria-label quando não há `labelledBy`. */
  label?: string;
  labelledBy?: string;
  variant?: "center" | "drawer" | "media";
  className?: string;
  children: ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

const PANEL_MOTION = {
  center: {
    initial: { opacity: 0, y: 24, scale: 0.97 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 16, scale: 0.98 },
  },
  media: {
    initial: { opacity: 0, scale: 0.94 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.96 },
  },
  drawer: {
    initial: { x: "100%" },
    animate: { x: 0 },
    exit: { x: "100%" },
  },
} as const;

/** Diálogo acessível: portal, Esc fecha, foco preso dentro e devolvido ao fechar. */
export const Modal = ({
  open,
  onClose,
  label,
  labelledBy,
  variant = "center",
  className,
  children,
}: ModalProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.({ preventScroll: true });
    };
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div
          className={cn(
            "fixed inset-0 z-[60] flex",
            variant === "drawer" ? "justify-end" : "items-end justify-center p-0 sm:items-center sm:p-6",
          )}
        >
          <motion.div
            aria-hidden="true"
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={labelledBy ? undefined : label}
            aria-labelledby={labelledBy}
            tabIndex={-1}
            data-lenis-prevent
            {...PANEL_MOTION[variant]}
            transition={{ type: "spring", damping: 30, stiffness: 320 }}
            className={cn(
              "relative z-10 overflow-y-auto overscroll-contain outline-none",
              variant === "drawer" && "h-full w-full max-w-md border-l border-line bg-coal",
              variant === "center" &&
                "max-h-[92dvh] w-full max-w-lg rounded-t-3xl border border-line bg-coal sm:rounded-3xl",
              variant === "media" && "max-h-[90dvh] w-full max-w-5xl",
              className,
            )}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export const ModalCloseButton = ({ onClick, className }: { onClick: () => void; className?: string }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Fechar"
    className={cn(
      "grid size-10 place-items-center rounded-full border border-line bg-night/80 text-white transition-colors hover:border-primary hover:text-primary",
      className,
    )}
  >
    <X className="size-5" aria-hidden="true" />
  </button>
);
